import React, { useMemo } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useSecondScreenData, SecondScreenContext } from '../../../contexts/SecondScreenContext';
import { useTabletopWarRoomAudio } from '../../../hooks/useTabletopWarRoomAudio';
import type { WidgetProps, TabletopTelemetryView } from '../../../types/second-screen.types';
import ActRailWidget from './ActRailWidget';
import DecisionTickerWidget from './DecisionTickerWidget';
import PublicWorldWidget from './PublicWorldWidget';
import PressureFrame from './war-room/PressureFrame';
import SystemTileGrid from './war-room/SystemTileGrid';
import AlertStream from './war-room/AlertStream';
import OnTheCall from './war-room/OnTheCall';
import OpenDecisions from './war-room/OpenDecisions';

// Trusted only once the adjudicator has actually produced a reading
// (pressureReason non-empty — the default run state is pressureLevel: 0,
// pressureReason: ''). Coarser than the adjudicator's own 5-level judgement,
// since raw telemetry severity alone can't distinguish "elevated" from
// "critical" (levels 2 and 4) — those need a read of the whole picture, not
// just today's alert list, which is exactly what the adjudicator is for.
function fallbackPressureFromTelemetry(telemetry: TabletopTelemetryView[]): 0 | 1 | 3 {
  if (telemetry.some((t) => t.severity === 'critical')) return 3;
  if (telemetry.some((t) => t.severity === 'warning')) return 1;
  return 0;
}

// The composed war-room scene (plan 06) — one full-bleed transparent widget
// replacing the five-widget grid framing, registered as `tabletop-war-room`.
// The five original tabletop widgets stay registered and untouched for
// anyone using a custom layout (plan 06 acceptance criteria); this widget
// reuses ActRailWidget, DecisionTickerWidget and PublicWorldWidget directly
// rather than rebuilding them, since all three already self-fetch from
// context with no props.
//
// War room is always dark regardless of the session's light/dark toggle —
// a deliberate departure from every other widget (plan 06 says so
// explicitly, this comment is that note). The three reused widgets above
// still branch on useSecondScreenData().theme internally, so rather than
// giving them a theme prop they don't have, this wraps them in a locally
// forced-dark Provider — only this subtree sees theme: 'dark'; every other
// widget on the screen still sees the real session theme.
const TabletopWarRoomWidget: React.FC<WidgetProps> = () => {
  const outer = useSecondScreenData();
  const tabletop = outer.state?.tabletop;
  // Only `state` and `theme` are ever read by anything in this subtree
  // (ActRailWidget/DecisionTickerWidget/PublicWorldWidget all destructure
  // just those two) — memoizing on the whole `outer` object recomputes on
  // every unrelated context event (a spotlight, a mind bubble, a cinematic
  // card elsewhere on the same second screen), since SecondScreenContext's
  // own value memo spans all of those fields, not just tabletop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const forcedDark = useMemo(() => ({ ...outer, theme: 'dark' as const }), [outer.state]);

  const hasAdjudicatorSignal = !!tabletop?.pressureReason;
  const pressureLevel = hasAdjudicatorSignal
    ? (tabletop!.pressureLevel)
    : fallbackPressureFromTelemetry(tabletop?.telemetry ?? []);
  const { muted, toggleMuted } = useTabletopWarRoomAudio(tabletop, pressureLevel);

  if (!tabletop) {
    return (
      <div className="flex items-center justify-center h-full text-white/30 bg-[#052831]" style={{ fontSize: '18px' }}>
        No active exercise
      </div>
    );
  }

  return (
    <SecondScreenContext.Provider value={forcedDark}>
      <PressureFrame pressureLevel={pressureLevel} simTimeMs={tabletop.simTimeMs}>
        <div className="flex flex-col w-full h-full overflow-hidden text-white">
          {/* Act rail — 12%, reused as-is (plan 03) */}
          <div style={{ height: '12%' }} className="flex-shrink-0 min-h-0">
            <ActRailWidget config={{}} />
          </div>

          {/* System status + alert stream — 46% */}
          <div style={{ height: '46%' }} className="flex-shrink-0 flex min-h-0 border-t border-white/10">
            <div className="flex-shrink-0 w-[30%] border-r border-white/10 px-3 py-2 min-h-0">
              <SystemTileGrid />
            </div>
            <div className="flex-1 px-3 py-2 min-h-0 min-w-0">
              <AlertStream />
            </div>
          </div>

          {/* Public world / on the call / open decisions — 26% */}
          <div style={{ height: '26%' }} className="flex-shrink-0 flex min-h-0 border-t border-white/10">
            <div className="flex-1 p-2 min-h-0 min-w-0">
              <PublicWorldWidget config={{}} />
            </div>
            <div className="flex-1 px-3 py-2 min-h-0 min-w-0 border-l border-white/10">
              <OnTheCall />
            </div>
            <div className="flex-1 px-3 py-2 min-h-0 min-w-0 border-l border-white/10">
              <OpenDecisions simTimeMs={tabletop.simTimeMs} />
            </div>
          </div>

          {/* Decision ticker — 16%, reused as-is (plan 03) */}
          <div style={{ height: '16%' }} className="flex-shrink-0 min-h-0 border-t border-white/10">
            <DecisionTickerWidget config={{}} />
          </div>
        </div>

        {/* Adjudicator paused (plan 04's facilitator override) — without this,
            pressureLevel/pressureReason freeze at their last real values and
            keep rendering as if live, with no indication the reading is stale. */}
        {tabletop.adjudicationSuspended && (
          <div
            className="absolute top-3 right-3 z-10 rounded-full bg-black/50 px-3 py-1.5 text-amber-300 uppercase tracking-widest"
            style={{ fontSize: '16px' }}
          >
            Reading paused
          </div>
        )}

        {/* z-10: above PressureFrame's ::after vignette/glow (z-index 1) */}
        <button
          onClick={toggleMuted}
          className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 rounded-full bg-black/50 hover:bg-black/70 px-3 py-2 text-white/80 transition-colors"
          style={{ fontSize: '16px' }}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          {muted ? 'Enable sound' : 'Sound on'}
        </button>
      </PressureFrame>
    </SecondScreenContext.Provider>
  );
};

export default TabletopWarRoomWidget;
